import { Component, OnInit, OnDestroy, ViewChild, ViewChildren, QueryList, ElementRef, AfterViewInit, NgZone } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Meta, Title } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { Casautomation } from 'src/app/services/casautomation.service';
import { switchMap, catchError } from 'rxjs/operators';
import { EMPTY, throwError } from 'rxjs';

interface WealthLensFeature {
  icon: string;
  title: string;
  description1: string;
  description2: string;
}

interface WealthLensFaq {
  question: string;
  answer: string;
}

declare function CallSmallCase(TranID: string, Token: string, cb: any, errorCb?: any): any;

@Component({
  selector: 'app-wealthLens-report',
  templateUrl: './wealthLens-report.component.html',
  styleUrls: ['./wealthLens-report.component.css'],
})
export class WealthLensReport implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('heroCta', { static: false }) heroCta!: ElementRef;
  @ViewChildren('otpBox') otpBoxes!: QueryList<ElementRef<HTMLInputElement>>;

  isLeadFormVisible = false;
  isModalVisible = false;
  submitted = false;
  openFaqIndex: number | null = null;
  showStickyBtn = false;
  transactionId = '';
  token = '';
  loadingSm = false;
  name: string = '';
  email: string = '';
  mobile: string = '';
  pan: string = '';
  pincode: string = '';
  reportType: string = '';
  reportTypeOptions = [
    { label: 'Aggressive', value: 'AGGRESSIVE' },
    { label: 'Moderate', value: 'MODERATE' },
    { label: 'Defensive', value: 'DEFENSIVE' }
  ];
  formSubmitted = false;
  errorMessage = '';
  successMessage = '';
  formLocked = false;
  rmLeadCode = '';
  utmSource = '';
  utmMedium = '';
  utmCampaign = '';
  alreadySubmitted = false;
  authToken = '';
  noDataFound = false;
  // --- OTP verification state (WealthLens flow only) ---
  isOtpVisible = false;
  otpDigits: string[] = ['', '', '', '', ''];
  otpVerifying = false;
  otpError = '';
  otpResendTimer = 0;
  canResendOtp = false;
  otpResending = false;
  private otpTimerInterval: any = null;
  scoreAnimated = false;
  animatedScore = '0.0';
  private scoreAnimationId: any = null;
  private readonly formRoute = '/wealth-report-form';
  private readonly reportRoute = '/wealth-report-view';

  features: WealthLensFeature[] = [
    {
      icon: 'overlap',
      title: 'Portfolio Overlap Analysis',
      description1: 'See how much duplication sits across your different funds.',
      description2:""
    },
    {
      icon: 'score',
      title: 'WealthLens Score',
      description1: 'One clear number for how spread out your risk really is.',
      description2: ""
    },
    {
      icon: 'shield',
      title: 'Risk Assessment',
      description1: 'Understand the real risk you are carrying, .',
      description2: "fund by fund"
    },
    {
      icon: 'bars',
      title: 'Asset Allocation Review',
      description1:
        `Equity, debt, hybrid - see the real mix,`, 
      description2:`not the assumed one.`,
    },
    {
      icon: 'gap',
      title: 'Gap Analysis',
      description1: 'Where your portfolio falls short of your actual goals.',
      description2: ""
    },
    {
      icon: 'trend',
      title: 'Expert Recommendations',
      description1:
        'What a Bajaj Capital advisor would suggest,',
      description2: "based on all of it."
    },
  ];

  privacyItems = [
    {
      title: 'MF Central integration',
      description:
        'Data is fetched through MF Central official, regulator-backed infrastructure - not scraped or stored elsewhere.',
    },
    {
      title: 'Never sold, never shared',
      description:
        'Your portfolio data is used only to generate your report and, if you choose, to inform an advisor conversation.',
    },
    {
      title: "You're always in control",
      description:
        'No commitment, no obligation - see your report first, decide what happens next.',
    },
  ];

  faqs: WealthLensFaq[] = [
    {
      question: 'Do I need to be an existing Bajaj Capital customer?',
      answer:
        'No. You can generate your WealthLens report even if you are new to Bajaj Capital.',
    },
    {
      question: 'What is MF Central, and is it safe?',
      answer:
        'MF Central is an official mutual fund industry platform used to fetch portfolio information securely.',
    },
    {
      question: 'How is this different from my regular MF statement?',
      answer:
        'A statement lists holdings. WealthLens turns those holdings into overlap, risk, allocation, gap, and recommendation insights.',
    },
    {
      question: 'Will someone contact me after I generate my report?',
      answer:
        'An advisor may contact you only to help you understand the report and next steps.',
    },
  ];

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private casService: Casautomation,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private ngZone: NgZone,
  ) {}

  launchMfCentral() {
    this.errorMessage = '';
    this.successMessage = '';
    if (!this.pan || !this.mobile || !this.name || !this.email) return;
    this.loadingSm = true;

    const authPayload = {
      pan: this.pan,
      email: this.email,
      mobile: this.mobile,
    };

    this.casService
      .generateTokenByPanEmail(authPayload)
      .pipe(
        switchMap((authRes: any) => {
          this.authToken = this.getJwtToken(authRes);

          if (!this.authToken) {
            return throwError(() => new Error('Auth Token Missing'));
          }

          this.saveReportSession();
          return this.casService.getFetchStatus({ pan: this.pan }, this.authToken);
        }),
        switchMap((statusRes: any) => {
          const fetchedStatus = statusRes?.data?.fetchedStatus;

          if (fetchedStatus === true) {
            this.loadingSm = false;
            this.saveReportSession();
            this.openOtpPopup();
            return EMPTY;
          }
          return this.casService.generateTokenSm(this.authToken);
        }),
        switchMap((tokenRes: any) => {
          this.token = tokenRes?.data?.token;
          const payload = {
            smallcaseToken: this.token,
            pan: this.pan,
            mobile: this.mobile,
          };
          return this.casService.generateTransactionSm(payload, this.authToken);
        }),
        catchError((error) => {
          this.formLocked = false;
          this.loadingSm = false;
          this.errorMessage = error.message === 'Auth Token Missing'
            ? 'Unable to authenticate this request. Please try again.'
            : 'Something went wrong. Please try again.';
          return EMPTY;
        }),
      )
      .subscribe((transactionRes: any) => {
        this.transactionId = transactionRes?.data?.result?.transactionId;

        if (!this.transactionId) {
          this.loadingSm = false;
          this.errorMessage = 'Unable to start report generation. Please try again.';
          return;
        }

        CallSmallCase(
          this.transactionId,
          this.token,
          () => {
            // SDK completes outside Angular's zone; re-enter so the UI updates.
            this.ngZone.run(() => this.verifyDataThenOpenReport());
          },
          (err: any) => {
            // User closed/cancelled the SDK, or the transaction errored.
            this.ngZone.run(() => this.onSmallcaseCancelled(err));
          }
        );
      });
  }

  ngOnInit(): void {
    this.titleService.setTitle('Bajaj Capital WealthLens Report');
    this.metaService.updateTag({
      name: 'description',
      content:
        'Get a personalised WealthLens analysis of your mutual fund portfolio in under 60 seconds.',
    });
    const queryParams = this.activatedRoute.snapshot.queryParamMap;
    this.rmLeadCode = (queryParams.get('ref') || '').trim();
    this.utmSource = (queryParams.get('utm_source') || '').trim();
    this.utmMedium = (queryParams.get('utm_medium') || '').trim();
    this.utmCampaign = (queryParams.get('utm_campaign') || '').trim();
    this.startScoreAnimation();
  }

  ngAfterViewInit(): void {
    if (this.heroCta?.nativeElement) {
      const observer = new IntersectionObserver(
        (entries) => {
          // When less than 50% of the hero CTA is visible, show sticky button
          entries.forEach((entry) => {
            this.showStickyBtn = entry.intersectionRatio < 0.5;
          });
        },
        { threshold: [0, 0.5, 1] }
      );
      observer.observe(this.heroCta.nativeElement);
    }
  }

  private startScoreAnimation(): void {
    const targetScore = 7.2;
    const duration = 1500; // ms, matches CSS transition duration
    const startTime = performance.now();

    setTimeout(() => {
      this.scoreAnimated = true;
    }, 300);

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime - 300; // delay to sync with ring
      if (elapsed < 0) {
        this.scoreAnimationId = requestAnimationFrame(animate);
        return;
      }
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic to match the CSS cubic-bezier roughly
      const eased = 1 - Math.pow(1 - progress, 3);
      const currentScore = eased * targetScore;
      this.animatedScore = currentScore.toFixed(1);

      if (progress < 1) {
        this.scoreAnimationId = requestAnimationFrame(animate);
      } else {
        this.animatedScore = targetScore.toFixed(1);
      }
    };

    this.scoreAnimationId = requestAnimationFrame(animate);
  }

  ngOnDestroy(): void {
    if (this.scoreAnimationId) {
      cancelAnimationFrame(this.scoreAnimationId);
    }
    this.clearOtpTimer();
  }

  showLeadForm(): void {
    this.openModal();

    // OLD FLOW (disconnected - kept for easy revert):
    // this.openLeadFormPage();
  }

  openModal(): void {
    this.isModalVisible = true;
    this.formSubmitted = false;
    this.noDataFound = false;
    this.errorMessage = '';
  }

  closeModal(): void {
    this.isModalVisible = false;
    this.noDataFound = false;
    this.errorMessage = '';
  }

  submitModalForm(modalForm: NgForm): void {
    this.formSubmitted = true;

    if (modalForm.invalid) {
      modalForm.form.markAllAsTouched();
      return;
    }

    this.launchMfCentral();
  }

  openLeadFormPage(): void {
    window.open(`${window.location.origin}${this.formRoute}`, '_blank', 'noopener');
  }

  submitReviewForm(reviewForm: NgForm): void {
    this.formSubmitted = true;

    if (reviewForm.invalid) {
      reviewForm.form.markAllAsTouched();
      return;
    }

    this.launchMfCentral();
  }

  toggleFaq(index: number): void {
    this.openFaqIndex = this.openFaqIndex === index ? null : index;
  }

  convertPanToUppercase(): void {
    this.pan = this.pan ? this.pan.toUpperCase() : '';
  }

  onlyLetters(event: KeyboardEvent): void {
    const charCode = event.which ? event.which : event.keyCode;

    if (
      !(charCode >= 65 && charCode <= 90) &&
      !(charCode >= 97 && charCode <= 122) &&
      charCode !== 32
    ) {
      event.preventDefault();
    }
  }

  onlyNumbers(event: KeyboardEvent): void {
    const charCode = event.which ? event.which : event.keyCode;

    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  onPasteName(event: ClipboardEvent): void {
    const pastedInput = event.clipboardData?.getData('text') || '';

    if (!/^[A-Za-z ]+$/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  onPasteNumber(event: ClipboardEvent): void {
    const pastedInput = event.clipboardData?.getData('text') || '';

    if (!/^\d+$/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  /**
   * Called when the smallcase SDK is closed/cancelled by the user or the
   * transaction errors out before completing. Without this the form stays
   * stuck on "Please wait..." forever. Reset it so it can be used again.
   */
  private onSmallcaseCancelled(err?: any): void {
    console.warn('Smallcase SDK cancelled or failed', err);
    this.loadingSm = false;
    this.formLocked = false;
    this.formSubmitted = false;
  }

  /**
   * After the SDK completes we submit the lead, then confirm the customer
   * actually has portfolio data before navigating. If the report API returns
   * no data (e.g. 404 / empty holdings), we never open the report view with
   * placeholder values — we show the "No current investment found" popup.
   */
  private verifyDataThenOpenReport(): void {
    this.insertLead();
    this.checkReportDataAvailability();
  }

  /** Submits the campaign lead (fire-and-forget for navigation purposes). */
  private insertLead(): void {
    const payload: any = {
      name: this.name,
      pan: this.pan,
      email: this.email,
      phone: this.mobile,
      pincode: this.pincode,
      transactionId: this.transactionId,
      source: 'bcl_campaign_mf_wealthLens',
      riskProfile: this.reportType,
    };

    if (this.rmLeadCode) {
      payload['rmLead'] = this.rmLeadCode;
    }

    if (this.utmSource) {
      payload['utmSource'] = this.utmSource;
    }

    if (this.utmMedium) {
      payload['utmMedium'] = this.utmMedium;
    }

    if (this.utmCampaign) {
      payload['utmCampaign'] = this.utmCampaign;
    }

    this.casService.insertMfcampaignlead(payload, this.authToken).subscribe({
      next: () => {
        this.saveReportSession();
      },
      error: (err) => {
        console.error('WealthLens lead submission failed', err);
      },
    });
  }

  /**
   * Checks whether report data exists for this customer. Navigates to the
   * report view only when real data is returned; otherwise surfaces the
   * no-data popup so we never show placeholder numbers.
   */
  private checkReportDataAvailability(): void {
    const panToUse = this.pan;
    const reportType = this.reportType || 'AGGRESSIVE';

    this.casService.getWealthLensReportData({ pan: panToUse, reportType }, this.authToken).subscribe({
      next: (res: any) => {
        const hasData =
          res?.statusCode === 200 &&
          res?.data &&
          Array.isArray(res.data.fundDetails) &&
          res.data.fundDetails.length > 0;

        if (hasData) {
          this.loadingSm = false;
          this.saveReportSession();
          this.resetToReportPreview();
          this.openReportPage();
        } else {
          this.showNoDataPopup();
        }
      },
      error: (err) => {
        // 404 / any failure means there is nothing to show.
        console.error('WealthLens report data unavailable', err);
        this.showNoDataPopup();
      },
    });
  }
  private openOtpPopup(): void {
    this.otpDigits = ['', '', '', '', ''];
    this.otpError = '';
    this.otpVerifying = false;
    this.isOtpVisible = true;
    this.isModalVisible = true;
    this.startOtpTimer();
  }

  /** Starts the 60s countdown; Resend stays disabled until it hits 0. */
  private startOtpTimer(): void {
    this.clearOtpTimer();
    this.otpResendTimer = 60;
    this.canResendOtp = false;

    this.otpTimerInterval = setInterval(() => {
      this.otpResendTimer--;
      if (this.otpResendTimer <= 0) {
        this.clearOtpTimer();
        this.canResendOtp = true;
      }
    }, 1000);
  }

  private clearOtpTimer(): void {
    if (this.otpTimerInterval) {
      clearInterval(this.otpTimerInterval);
      this.otpTimerInterval = null;
    }
  }

  /** Resend OTP: re-hits the generate-OTP API, then restarts the timer. */
  resendOtp(): void {
    if (!this.canResendOtp || this.otpResending) return;

    this.otpResending = true;
    this.otpError = '';
    this.otpDigits = ['', '', '', '', ''];

    this.casService.getFetchStatus({ pan: this.pan }, this.authToken).subscribe({
      next: () => {
        this.otpResending = false;
        this.startOtpTimer();
        this.focusOtpBox(0);
      },
      error: (err) => {
        console.error('WealthLens OTP resend failed', err);
        this.otpResending = false;
        this.otpError = 'Could not resend OTP. Please try again.';
        // Let them try again immediately.
        this.canResendOtp = true;
      },
    });
  }

  get isOtpComplete(): boolean {
    return this.otpDigits.every((d) => d !== '' && /^[0-9]$/.test(d));
  }

  /** trackBy keeps the input elements stable so typing never loses focus. */
  trackByIndex(index: number): number {
    return index;
  }

  /** Focuses the OTP box at the given index, if it exists. */
  private focusOtpBox(index: number): void {
    const boxes = this.otpBoxes?.toArray();
    boxes?.[index]?.nativeElement.focus();
  }

  /** Keeps a single numeric digit per box and auto-advances focus. */
  onOtpInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const digit = (input.value || '').replace(/[^0-9]/g, '').slice(-1);

    this.otpDigits[index] = digit;
    input.value = digit;
    this.otpError = '';

    if (digit && index < this.otpDigits.length - 1) {
      this.focusOtpBox(index + 1);
    }
  }

  /** Backspace on an empty box moves focus to the previous box. */
  onOtpKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
      this.focusOtpBox(index - 1);
    }
  }

  /** Allows pasting a full 5-digit OTP into any box. */
  onOtpPaste(event: ClipboardEvent): void {
    const pasted = (event.clipboardData?.getData('text') || '').replace(/[^0-9]/g, '');
    if (!pasted) return;
    event.preventDefault();

    const digits = pasted.slice(0, this.otpDigits.length).split('');
    this.otpDigits = this.otpDigits.map((_, i) => digits[i] || '');
    this.otpError = '';

    const nextIndex = Math.min(digits.length, this.otpDigits.length - 1);
    setTimeout(() => this.focusOtpBox(nextIndex));
  }

  /** Verifies the entered OTP. true -> report page, false -> invalid message. */
  verifyOtp(): void {
    if (!this.isOtpComplete || this.otpVerifying) return;

    this.otpVerifying = true;
    this.otpError = '';

    const payload = {
      mobileNo: this.mobile,
      otp: this.otpDigits.join(''),
    };

    this.casService.verifyWealthLensOTP(payload, this.authToken).subscribe({
      next: (res: any) => {
        // Response shape: { statusCode, data: { verified: boolean }, message, success }
        const verified = res?.data?.verified === true;
        this.otpVerifying = false;

        if (verified) {
          this.clearOtpState();
          this.resetToReportPreview();
          this.openReportPage();
        } else {
          this.otpError = res?.message || 'Invalid OTP. Please try again.';
        }
      },
      error: (err) => {
        console.error('WealthLens OTP verification failed', err);
        this.otpVerifying = false;
        this.otpError = err?.error?.message || 'Invalid OTP. Please try again.';
      },
    });
  }

  /**
   * Called when the OTP is verified — fully tears down the OTP popup before
   * navigating to the report.
   */
  private clearOtpState(): void {
    this.clearOtpTimer();
    this.isOtpVisible = false;
    this.otpDigits = ['', '', '', '', ''];
    this.otpError = '';
    this.otpVerifying = false;
    this.canResendOtp = false;
    this.otpResending = false;
    this.isModalVisible = false;
  }

  /**
   * X on the OTP popup: go back to the lead form with the user's details
   * still filled in, so they can review and correct whatever they got wrong.
   */
  closeOtpPopup(): void {
    this.clearOtpTimer();
    this.isOtpVisible = false;
    this.otpDigits = ['', '', '', '', ''];
    this.otpError = '';
    this.otpVerifying = false;
    this.canResendOtp = false;
    this.otpResending = false;
    this.loadingSm = false;
    // Keep name/pan/email/mobile/reportType intact and show the form again.
    this.submitted = false;
    this.alreadySubmitted = false;
    this.noDataFound = false;
    this.formSubmitted = false;
    this.isModalVisible = true;
  }

  /** Shows the "No current investment found" popup and stops the spinner. */
  private showNoDataPopup(): void {
    this.loadingSm = false;
    this.formLocked = false;
    this.noDataFound = true;
    this.isModalVisible = true;
  }

  /**
   * "OK" on the no-data popup: clear the form and reopen a fresh, empty
   * lead form so the user can try again with different details.
   */
  reloadForm(): void {
    this.noDataFound = false;
    this.resetFormFields();
    this.openModal();
  }

  /**
   * Cross (×) on the no-data popup: close the whole modal completely and
   * clear the form.
   */
  closeNoDataPopup(): void {
    this.noDataFound = false;
    this.resetFormFields();
    this.closeModal();
  }

  /** Clears all lead-form fields and transient submission state. */
  private resetFormFields(): void {
    this.name = '';
    this.email = '';
    this.mobile = '';
    this.pan = '';
    this.pincode = '';
    this.reportType = '';
    this.formSubmitted = false;
    this.submitted = false;
    this.alreadySubmitted = false;
    this.loadingSm = false;
    this.formLocked = false;
    this.errorMessage = '';
    this.successMessage = '';
  }

  private getJwtToken(response: any): string {
    return response?.data?.token ||
      response?.data?.jwtToken ||
      response?.data?.accessToken ||
      response?.token ||
      response?.jwtToken ||
      response?.accessToken ||
      '';
  }

  private saveReportSession(): void {
    localStorage.setItem('wealthLensAuthToken', this.authToken);
    localStorage.setItem('wealthLensReportUser', JSON.stringify({
      name: this.name,
      pan: this.maskPan(this.pan),
      rawPan: this.pan,
      email: this.email,
      mobile: this.mobile,
      transactionId: this.transactionId,
      reportType: this.reportType,
      rmLeadCode:this.rmLeadCode,
    }));
  }

  private maskPan(pan: string): string {
    if (!pan || pan.length < 4) {
      return 'XXXXX4321F';
    }

    return `XXXXX${pan.slice(-5)}`;
  }

  private openReportPage(): void {
    // Navigate within the same tab using Angular Router — no pop-up blocker issues
    this.router.navigate([this.reportRoute]);
  }

  private resetToReportPreview(): void {
    this.isLeadFormVisible = false;
    this.submitted = false;
    this.alreadySubmitted = false;
    this.formSubmitted = false;
  }
}
